import {ref, computed, watchEffect} from "vue";
import {useRoute} from "vue-router";
import useUserSafe from "@/composables/useUserSafe";

const QUALIFYING_ROLES = ["participant", "organizer", "speaker", "sponsor", "session-chair"];

const EVENT_TYPE_NAMES = {
  masterclass: "VIZBI Masterclass",
  workshop: "VIZBI Workshop",
  showcase: "VIZBI Showcase"
};

function detectEventKey(sessionTitle) {
  if (!sessionTitle) return null;
  if (/masterclass/i.test(sessionTitle)) return "masterclass";
  if (/workshop/i.test(sessionTitle)) return "workshop";
  if (/showcase/i.test(sessionTitle)) return "showcase";
  return null;
}

function userHasQualifyingRole(userEvents, canonicalName) {
  const eventEntry = userEvents?.[canonicalName];
  if (!eventEntry || typeof eventEntry !== "object") return false;
  for (const deliveryRoles of Object.values(eventEntry)) {
    if (!Array.isArray(deliveryRoles)) continue;
    if (deliveryRoles.some((role) => QUALIFYING_ROLES.includes(role))) return true;
  }
  return false;
}

async function fetchEventsData(year) {
  if (!year) return null;
  try {
    const module = await import(`@/assets/years/${year}/Registration/events.json`);
    return module.default;
  } catch {
    return null;
  }
}

export default function useJoinLink(overrideYear) {
  const route = useRoute();
  const {user} = useUserSafe();
  const eventsData = ref(null);
  const year = overrideYear
    ? computed(() => String(overrideYear))
    : computed(() => route?.params?.year || "");
  watchEffect(async () => {
    eventsData.value = await fetchEventsData(year.value);
  });
  function getJoinLink(sessionTitle) {
    const eventKey = detectEventKey(sessionTitle);
    if (!eventKey) return null;
    const userEvents = user.value?.publicMetadata?.events;
    const canonicalName = `${eventKey}-${year.value}`;
    if (!userHasQualifyingRole(userEvents, canonicalName)) return null;
    const typeName = EVENT_TYPE_NAMES[eventKey];
    return eventsData.value?.[typeName]?.Virtual?.joinLink || null;
  }
  return {getJoinLink};
}
